import React from "react";
import { fetchBalanceVerify } from "../api";
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { t } from '../utils/translations';
import {
  Check,
  LayoutDashboard,
  Database,
  ShieldCheck,
  SlidersHorizontal,
  BadgeInfo,
  Activity,
  CircleAlert,
} from 'lucide-react';
import { cn, tableTheme as T, analysisTone } from "./ui/analysisTableTheme.js";
import { fmtTokenAmountFixed } from "../utils/formatNumbers";

const normalizeTaxCountryCode = (country) => {
  const c = String(country || "DE").trim().toUpperCase().replace(/\s+/g, "_");
  if (["UK", "GB", "UNITED_KINGDOM", "UNITED KINGDOM"].includes(c)) return "UK";
  return c;
};

const formatUkTaxYearShortLabel = (year) => {
  const y = Number(year);
  if (!Number.isFinite(y) || y <= 0) return String(year || "");
  return `${y}/${String((y + 1) % 100).padStart(2, "0")}`;
};

const formatTaxYearRangeLabel = (selectedCountry = "DE", taxYear, language = "de") => {
  const yearInt = Number(taxYear);
  if (!Number.isFinite(yearInt) || yearInt <= 0) return String(taxYear || "");

  const cc = normalizeTaxCountryCode(selectedCountry);
  const startTs =
    cc === "UK"
      ? Math.floor(Date.UTC(yearInt, 3, 6, 0, 0, 0) / 1000)
      : Math.floor(Date.UTC(yearInt, 0, 1, 0, 0, 0) / 1000);
  const endExclusiveTs =
    cc === "UK"
      ? Math.floor(Date.UTC(yearInt + 1, 3, 6, 0, 0, 0) / 1000)
      : Math.floor(Date.UTC(yearInt + 1, 0, 1, 0, 0, 0) / 1000);

  const locale = language === "en" ? "en-GB" : "de-DE";
  const fmt = (ts) =>
    new Date(ts * 1000).toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });

  if (cc === "UK") {
    return `UK Tax Year ${formatUkTaxYearShortLabel(yearInt)}: ${fmt(startTs)} – ${fmt(endExclusiveTs - 1)}`;
  }

  return String(yearInt);
};

export default function BalanceVerifierMulti({ 
  datasetId, 
  walletSet, 
  year,
  useClean = false, 
  includeStakedVet = false,
  loadFromSnapshot = false,
  snapshotData = null,  // 🚀 NEU: Verifier-Daten aus Snapshot
  dataSource = "live",  // ✅ NEU: "snapshot" | "live" | "preview" für UI-Anzeige
  onRecalculate = undefined,  // ✅ NEU: Optional: Callback für "Neu prüfen" (z.B. Preview-Analyse neu starten)
  softMissing = {},  // ✅ FIX GPT 5: Optional für Soft-Debug-Infos
  // ✅ NEU: Tax-Logic + Cost-Basis-Methode
  taxCountryCode = null,
  taxCountryName = null,
  taxCalculationMethod = null
}) {
  const { language } = useLanguage();
  const tone = analysisTone.indigo;

  // ✅ IMPORTANT: translations.js defaults to 'de' if language not passed
  const tr = React.useCallback((key) => t(key, language), [language]);

  // Token-Balances in Tabellen: immer 6 Nachkommastellen fixed (kein Trim)
  const fmtBalance = React.useCallback((x) => {
    const n = Number(x);
    return Number.isFinite(n) ? fmtTokenAmountFixed(n, 6) : "—";
  }, []);

  const isSyntheticHiddenToken = React.useCallback((token) => {
    const t = String(token || '').trim().toUpperCase();
    if (!t) return false;

    return (
      t === 'VOT3_GOV_LOCKED' ||
      t.startsWith('JUICY_SUPPLIED:') ||
      t.startsWith('JUICY_DEBT:')
    );
  }, []);
  
  // NOTE:
  // This BalanceVerifier is a reconciliation view (Reported/on-chain vs Calculated/FIFO).
  // We intentionally do NOT compute or show any subjective "score" or rating.
  
  const [state, setState] = React.useState({ 
    loading: false, 
    data: null, 
    error: null 
  });

  // Toggle-States
  const [localUseClean, setLocalUseClean] = React.useState(true); // 🚀 NEU: Clean-View standardmäßig aktiviert
  const [localIncludeStaked, setLocalIncludeStaked] = React.useState(includeStakedVet);

  // ✅ FIX: Einheitliche Adress-Normalisierung für Cache-Key-Match
  // Extrahiere Wallets aus mehreren Quellen (walletSet, snapshotData, etc.)
  const addresses = React.useMemo(() => {
    // 1. Aus walletSet (explizit übergeben)
    const mainAddrs = walletSet?.addresses || walletSet || [];
    const delegateAddrs = walletSet?.delegateAddresses || [];
    
    // 2. Aus snapshotData (wenn vorhanden)
    const snapshotAddrs = snapshotData?.addresses || [];
    
    // 3. Kombiniere alle Quellen
    const allAddrs = [...mainAddrs, ...delegateAddrs, ...snapshotAddrs];
    if (!Array.isArray(allAddrs)) return [];
    
    // 4. Normalisiere: lowercase, trim, unique, sortiert - MUSS mit Backend identisch sein!
    const normalized = [...new Set(allAddrs.map(a => String(a).trim().toLowerCase()))].filter(Boolean).sort();
    
    // ✅ FIX: Wenn keine Adressen gefunden, aber snapshotData vorhanden, versuche aus fifoTotals/onchainTotals zu extrahieren
    if (normalized.length === 0 && snapshotData) {
            // Die Adressen könnten in den Totals-Objekten sein (als Keys oder in nested Strukturen)
      // Für jetzt geben wir eine leere Liste zurück - die Komponente sollte trotzdem funktionieren
    }
    
    return normalized;
  }, [walletSet, snapshotData]);

  async function run() {
    try {
      setState(s => ({ ...s, loading: true, error: null }));
      
      // ✅ FIX B: API-Call liest aus Cache (nicht aus persistierten Tabellen)
      // Der Backend-Endpoint /api/balance-verifier nutzt den Cache aus fifoTotals.js
            
      // ✅ FIX Schritt 2: datasetId IMMER mitsenden, walletSet nur wenn vorhanden
      const addressesArray = addresses || [];
      const payload = {
        datasetId,  // IMMER mitsenden
        year,
        includeStakedVet: false, // Button ist reine UI-Umschaltung; Backend-Basis bleibt stabil (liquid VET)
        useClean: false, // 🚀 NEU: Clean-View ist jetzt nur noch Frontend-Filtering
        epsilon: 1e-8
      };
      
      // walletSet nur mitsenden wenn Adressen vorhanden
      if (addressesArray.length > 0) {
        payload.walletSet = { addresses: addressesArray };
      }
      
      const data = await fetchBalanceVerify(payload);
      
      setState({ loading: false, data, error: null });
    } catch (err) {
                  setState({ loading: false, data: null, error: String(err?.message || err) });
    }
  }

  // ✅ Preview + Live nutzen immer eine stabile liquid-VET Basis.
  // Der Stargate-Button darf KEINE neue API-Abfrage auslösen, weil sonst andere Token
  // durch frische Live-/Cache-Daten mitwechseln können. Die Umschaltung passiert unten
  // rein lokal und ausschließlich für die VET-Zeile.
  React.useEffect(() => {
    if (snapshotData) {
      setState((s) => (
        s.data === snapshotData && !s.loading && !s.error
          ? s
          : { loading: false, data: snapshotData, error: null }
      ));
      return;
    }

    if (datasetId && addresses.length && year && !state.data) {
      run();
    }
  }, [datasetId, JSON.stringify(addresses), year, snapshotData]);

  const d = React.useMemo(() => {
    const hasValidRows = (rows) =>
      Array.isArray(rows) &&
      rows.some((r) => {
        const token = String(
          r?.token ||
            r?.asset ||
            r?.symbol ||
            r?.tokenSymbol ||
            r?.token_symbol ||
            ""
        ).trim();
        return Boolean(token);
      });

    const fromMulti = (source) => {
      const multi = source?.balanceVerifierMulti;
      if (!multi) return null;

      const multiRows = Array.isArray(multi.rows) ? multi.rows : [];
      const sourceDiffs = Array.isArray(source.diffs) ? source.diffs : [];

      const rows = hasValidRows(multiRows)
        ? multiRows
        : hasValidRows(sourceDiffs)
          ? sourceDiffs
          : multiRows.length
            ? multiRows
            : sourceDiffs;

      return {
        ...source,
        diffs: rows,
        onchainTotals: multi.onchainTotals || source.onchainTotals || {},
        onchainTotalsWithStaked:
          multi.onchainTotalsWithStaked ||
          source.onchainTotalsWithStaked ||
          source.onchainWithStakedTotals ||
          {},

        fifoTotals: multi.fifoTotals || source.fifoTotalsWalletOnly || source.fifoTotals || {},
        fifoTotalsFull: multi.fifoTotalsFull || source.fifoTotalsFull || source.fifoTotals || {},
        fifoTotalsWalletOnly:
          multi.fifoTotalsWalletOnly ||
          source.fifoTotalsWalletOnly ||
          multi.fifoTotals ||
          source.fifoTotals ||
          {},
        fifoTotalsClean: multi.fifoTotalsClean || source.fifoTotalsClean || source.holdingsClean || {},

        turnoverByToken: source.turnoverByToken || multi.turnoverByToken || {},
        exchangeBalancesByToken: source.exchangeBalancesByToken || multi.exchangeBalancesByToken || {},
      };
    };

    const sd = state.data;
    return fromMulti(sd) || sd || fromMulti(snapshotData) || snapshotData || null;
  }, [state.data, snapshotData]);

  // ✅ FIX D: Defensive Defaults - defensive Daten-Normalisierung statt Schema-Strictness
  const normalizedData = React.useMemo(() => {
    if (!d) return null;

    const safeNum = (v, fallback = 0) => (Number.isFinite(Number(v)) ? Number(v) : fallback);

    const normalizeTokenKey = (v) => String(v || "").trim().toUpperCase();

    const readTokenTotal = (obj, token) => {
      if (!obj || typeof obj !== 'object') return null;
      const target = String(token || '').trim().toUpperCase();
      if (!target) return null;

      const direct = obj[target] ?? obj[target.toLowerCase()];
      if (direct !== undefined && direct !== null && direct !== '') {
        const n = Number(direct);
        return Number.isFinite(n) ? n : null;
      }

      for (const [key, value] of Object.entries(obj)) {
        if (String(key || '').trim().toUpperCase() !== target) continue;
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
      }

      return null;
    };

    const buildRowsFromTotals = () => {
      const onchainTotals = d.onchainTotals || {};
      const fifoTotals = d.fifoTotalsWalletOnly || d.fifoTotals || {};
      const fifoFullTotals = d.fifoTotalsFull || fifoTotals || {};
      const exchangeBalances = d.exchangeBalancesByToken || {};
      const turnover = d.turnoverByToken || {};

      const tokens = new Set([
        ...Object.keys(onchainTotals),
        ...Object.keys(fifoTotals),
        ...Object.keys(fifoFullTotals),
        ...Object.keys(exchangeBalances),
        ...Object.keys(turnover),
      ].map(normalizeTokenKey).filter(Boolean));

      return [...tokens].map((token) => {
        const onchain = readTokenTotal(onchainTotals, token) ?? 0;
        const fifo = readTokenTotal(fifoTotals, token) ?? 0;
        const fifo_full = readTokenTotal(fifoFullTotals, token) ?? fifo;
        const exchange_est = readTokenTotal(exchangeBalances, token) ?? 0;
        const delta = onchain - fifo;

        return {
          token,
          onchain,
          fifo,
          fifo_walletOnly: fifo,
          fifo_full,
          exchange_est,
          delta,
          turnover: readTokenTotal(turnover, token) ?? 0,
          status: Math.abs(delta) <= 1e-8 ? "match" : delta > 0 ? "onchain_higher" : "fifo_higher",
        };
      });
    };

    const rawDiffs = Array.isArray(d.diffs) ? d.diffs : [];
    const hasValidDiffRows = rawDiffs.some((r) => normalizeTokenKey(
      r?.token || r?.asset || r?.symbol || r?.tokenSymbol || r?.token_symbol
    ));

    const diffs = hasValidDiffRows ? rawDiffs : buildRowsFromTotals();

    // Optional: Backend kann Turnover pro Token liefern (für Δ% Aktivität)
    const turnoverByToken =
      (d.turnoverByToken && typeof d.turnoverByToken === "object") ? d.turnoverByToken : {};

    const resolveTurnover = (token) => {
      const key = String(token || "UNKNOWN").trim().toUpperCase();

      // Turnover kann entweder als Zahl oder als { turnover: Zahl } kommen
      const directRaw = turnoverByToken?.[key];
      const direct = safeNum((directRaw && typeof directRaw === 'object') ? directRaw.turnover : directRaw, 0);
      if (direct > 0) return direct;

      // "B3TR/VOT3" => B3TR + VOT3
      if (key.includes('/')) {
        return key
          .split('/')
          .map(s => s.trim().toUpperCase())
          .filter(Boolean)
          .reduce((sum, part) => {
            const raw = turnoverByToken?.[part];
            const val = safeNum((raw && typeof raw === 'object') ? raw.turnover : raw, 0);
            return sum + val;
          }, 0);
      }

      return 0;
    };

    const calcPctMove = (delta, turnover) => {
      const t = safeNum(turnover, 0);
      if (t <= 1e-12) return null;
      return (Math.abs(safeNum(delta, 0)) / t) * 100;
    };

    const fifoVetLiquid =
      readTokenTotal(d.balanceVerifierMulti?.fifoTotalsFull, 'VET') ??
      readTokenTotal(d.fifoTotalsFull, 'VET') ??
      readTokenTotal(d.fifoTotals, 'VET');

    const fifoVetTotal =
      readTokenTotal(d.balanceVerifierMulti?.fifoTotalsClean, 'VET') ??
      readTokenTotal(d.fifoTotalsClean, 'VET');

    const onchainVetLiquid = readTokenTotal(d.onchainTotals, 'VET');
    const onchainVetWithStaked = readTokenTotal(d.onchainTotalsWithStaked, 'VET');

    // Aktuell gestaktes VET wird bevorzugt aus den zwei FIFO-Pools abgeleitet:
    // - full/liquid: Stargate-Deposits sind aus der sichtbaren Wallet-Balance rausgerechnet
    // - clean/total: Stargate-Deposits bleiben als Gesamtposition erhalten
    const stakedVetFromFifo =
      fifoVetLiquid != null && fifoVetTotal != null
        ? Math.max(0, fifoVetTotal - fifoVetLiquid)
        : 0;

    const stakedVetFromOnchain =
      onchainVetLiquid != null && onchainVetWithStaked != null
        ? Math.max(0, onchainVetWithStaked - onchainVetLiquid)
        : 0;

    const derivedStakedVet = stakedVetFromFifo > 1e-8 ? stakedVetFromFifo : stakedVetFromOnchain;

    const statusFromDelta = (delta) => {
      const x = safeNum(delta, 0);
      if (Math.abs(x) <= 1e-8) return 'match';
      return x > 0 ? 'onchain_higher' : 'fifo_higher';
    };

    const normalizedDiffs = diffs
      .map((diff) => {
        const token = normalizeTokenKey(
          diff?.token ||
            diff?.asset ||
            diff?.symbol ||
            diff?.tokenSymbol ||
            diff?.token_symbol
        );

        let onchain = safeNum(diff?.onchain, 0);
        let fifo_full = safeNum(diff?.fifo_full ?? diff?.fifoFull ?? 0, 0);
        let fifo_walletOnly = safeNum(
          diff?.fifo_walletOnly ??
            diff?.fifoWalletOnly ??
            diff?.fifo,
          0
        );
        const exchange_est = safeNum(diff?.exchange_est ?? diff?.exchangeEst ?? 0, 0);

        if (!token && Math.abs(onchain) <= 1e-12 && Math.abs(fifo_walletOnly) <= 1e-12) {
          return null;
        }

        let fifo = fifo_walletOnly;
        let fifo_mode = 'wallet_only';

        if (exchange_est > 1e-8 && fifo_full > 0) {
          const dWallet = Math.abs(onchain - fifo_walletOnly);
          const dFull = Math.abs(onchain - fifo_full);
          if (dFull + 1e-12 < dWallet) {
            fifo = fifo_full;
            fifo_mode = 'full';
          }
        }

        const tokenKey = token;

        // Stargate-VET ist eine reine Anzeigeumschaltung: exakt eine Zeile ändern, keine Datenquelle wechseln.
        const includeDerivedStakedVet = tokenKey === 'VET' && localIncludeStaked && derivedStakedVet > 1e-8;
        if (includeDerivedStakedVet) {
          const onchainTotal =
            onchainVetWithStaked != null && onchainVetWithStaked > onchain + 1e-8
              ? onchainVetWithStaked
              : onchain + derivedStakedVet;

          onchain = onchainTotal;
          fifo_full = fifo_full + derivedStakedVet;
          fifo_walletOnly = fifo_walletOnly + derivedStakedVet;
          fifo = fifo + derivedStakedVet;
        }

        const delta = onchain - fifo;

        const turnover = safeNum(diff?.turnover, 0) || resolveTurnover(token || tokenKey);
        const turnoverOperational = safeNum(diff?.turnoverOperational, turnover);
        const turnoverBreakdown = diff?.turnoverBreakdown || {};

        const pct_move = calcPctMove(delta, turnover);

        return {
          token: token || 'UNKNOWN',
          onchain,
          fifo,
          fifo_mode,
          fifo_walletOnly,
          fifo_full,
          exchange_est,
          delta,
          turnover,
          turnoverOperational,
          turnoverBreakdown,
          pct_move,
          status: includeDerivedStakedVet ? statusFromDelta(delta) : (diff?.status || statusFromDelta(delta)),
          staked_vet_included: includeDerivedStakedVet,
          staked_vet_amount: includeDerivedStakedVet ? derivedStakedVet : 0,
          lp_breakdown: Array.isArray(diff?.lp_breakdown) ? diff.lp_breakdown : [],
        };
      })
      .filter(Boolean)
      .filter((row) => {
        if (row.token !== "UNKNOWN") return true;
        return (
          Math.abs(Number(row.onchain || 0)) > 1e-12 ||
          Math.abs(Number(row.fifo || 0)) > 1e-12 ||
          Math.abs(Number(row.delta || 0)) > 1e-12
        );
      });

    return {
      ...d,
      diffs: normalizedDiffs,
      turnoverByToken,
      fifoTotals: d.fifoTotals || {},
      onchainTotals: d.onchainTotals || {},
      addresses: Array.isArray(d.addresses) ? d.addresses : [],
    };
  }, [d, localIncludeStaked]);

  const visibleDiffsBase = React.useMemo(() => {
    const rows = Array.isArray(normalizedData?.diffs) ? normalizedData.diffs : [];
    return rows.filter((row) => !isSyntheticHiddenToken(row?.token));
  }, [normalizedData, isSyntheticHiddenToken]);

  const hiddenSyntheticCount = React.useMemo(() => {
    const rows = Array.isArray(normalizedData?.diffs) ? normalizedData.diffs : [];
    return Math.max(0, rows.length - visibleDiffsBase.length);
  }, [normalizedData, visibleDiffsBase]);

  // ✅ FIX E: Hilfsfunktion für sichere Zahl-Konvertierung
  const co = (n) => Number.isFinite(Number(n)) ? Number(n) : 0;

  // ✅ NEU: Smart Formatting
  const fmtPctSmart = (v) => {
    if (v == null) return "—";
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    const a = Math.abs(n);
    if (a > 0 && a < 0.01) return "<0.01%";
    if (a < 1) return `${n.toFixed(3)}%`;
    return `${n.toFixed(2)}%`;
  };

  const fmtTurnover = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return "—";
    if (n >= 1e6) return n.toFixed(0);
    if (n >= 1e3) return n.toFixed(2);
    return n.toFixed(4);
  };

  const fmtTokenAmount = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return fmtTokenAmountFixed(n, 6);
  };

  // ✅ NEU: Helper für gekürzte Adressen (LP-Pools)
  const shortAddr = (addr) => {
    const s = String(addr || '');
    if (!s) return '';
    const x = s.trim();
    if (x.length <= 12) return x;
    return `${x.slice(0, 6)}…${x.slice(-4)}`;
  };

  const ToggleChip = ({ checked, disabled = false, onClick, label, hint = null }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "inline-flex shrink-0 items-center gap-2.5 rounded-xl border px-3 py-2 text-left shadow-sm transition-all duration-200",
        checked
          ? "border-indigo-200 bg-indigo-50 text-indigo-900 ring-1 ring-indigo-100"
          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
        disabled ? "cursor-not-allowed opacity-60" : "hover:-translate-y-0.5",
      ].join(" ")}
    >
      <span
        className={[
          "inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-all duration-200",
          checked
            ? "border-indigo-500 bg-indigo-500 text-white shadow-sm"
            : "border-slate-300 bg-white text-transparent",
        ].join(" ")}
      >
        <Check className="h-3 w-3" />
      </span>

      <span className="min-w-0 leading-tight">
        <span className="block whitespace-nowrap text-[12px] font-semibold">{label}</span>
        {hint ? <span className="mt-0.5 block text-[10px] text-slate-500">{hint}</span> : null}
      </span>
    </button>
  );

  const NoticePanel = ({ tone = "slate", icon: Icon = BadgeInfo, title, children }) => {
    const toneMap = {
      slate: "border-slate-200/80 bg-slate-50/80 text-slate-800",
      sky: "border-sky-200/80 bg-sky-50/80 text-sky-900",
      indigo: "border-indigo-200/80 bg-indigo-50/80 text-indigo-900",
      emerald: "border-emerald-200/80 bg-emerald-50/80 text-emerald-900",
      amber: "border-amber-200/80 bg-amber-50/80 text-amber-900",
      rose: "border-rose-200/80 bg-rose-50/80 text-rose-900",
    };

    return (
      <div className={`rounded-2xl border px-4 py-4 shadow-sm ${toneMap[tone] || toneMap.slate}`}>
        <div className="flex items-start gap-3">
          <div className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-white/50 bg-white/60 shadow-sm">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">{title}</div>
            <div className="mt-1 text-sm leading-6">{children}</div>
          </div>
        </div>
      </div>
    );
  };

  const SummaryMetric = ({ dotClass, label, value, hint = null }) => (
    <div className="rounded-xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/70 px-3.5 py-3 shadow-sm ring-1 ring-white/70">
      <div className="flex items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${dotClass}`} />
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          {label}
        </span>
      </div>
      <div className="mt-2.5 text-lg font-semibold tracking-tight text-slate-900">{value}</div>
      {hint ? <div className="mt-1 text-[11px] leading-5 text-slate-500">{hint}</div> : null}
    </div>
  );

  const StatusBadge = ({ status, compact = false }) => {
    const map = {
      match: {
        label: tr("balanceVerifier.status.match"),
        wrap: "border-emerald-200 bg-emerald-50 text-emerald-700 ring-emerald-100/70",
        dot: "bg-emerald-500",
      },
      onchain_higher: {
        label: tr("balanceVerifier.status.onchainHigher"),
        wrap: "border-amber-200 bg-amber-50 text-amber-800 ring-amber-100/70",
        dot: "bg-amber-500",
      },
      fifo_higher: {
        label: tr("balanceVerifier.status.fifoHigher"),
        wrap: "border-indigo-200 bg-indigo-50 text-indigo-700 ring-indigo-100/70",
        dot: "bg-indigo-500",
      },
    };

    const cfg = map[status];
    if (!cfg) return null;

    return (
      <span
        className={[
          "inline-flex items-center gap-1.5 rounded-full border font-semibold shadow-sm ring-1",
          compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-[11px]",
          cfg.wrap,
        ].join(" ")}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
        <span>{cfg.label}</span>
      </span>
    );
  };

  const InfoItem = ({ title, children, tone = "slate", icon: Icon = BadgeInfo, className = "" }) => {
    const toneMap = {
      slate: "border-slate-200/80 bg-slate-50 text-slate-600",
      emerald: "border-emerald-200/80 bg-emerald-50 text-emerald-700",
      amber: "border-amber-200/80 bg-amber-50 text-amber-700",
      indigo: "border-indigo-200/80 bg-indigo-50 text-indigo-700",
    };

    const iconWrap = toneMap[tone] || toneMap.slate;

    return (
      <div
        className={cn(
          "group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white px-4 py-4 shadow-sm ring-1 ring-white/70 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md",
          className
        )}
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-80" />

        <div className="flex items-start gap-3">
          <div
            className={cn(
              "mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border shadow-sm",
              iconWrap
            )}
          >
            <Icon className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              {title}
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              {children}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const formattedSnapshotTs = snapshotData?.timestamp
    ? new Date(snapshotData.timestamp).toLocaleString(language === "en" ? "en-US" : "de-DE")
    : null;

  const verifierMeta = snapshotData?.meta ?? d?.meta ?? normalizedData?.meta ?? null;
  const onchainAsOfTs =
    verifierMeta?.onchainAsOfTs ??
    snapshotData?.onchainAsOfTs ??
    normalizedData?.onchainAsOfTs ??
    null;
  const fifoAsOfTs =
    verifierMeta?.fifoAsOfTs ??
    onchainAsOfTs ??
    null;
  const onchainMode =
    verifierMeta?.onchainMode ??
    snapshotData?.onchainMode ??
    normalizedData?.onchainMode ??
    null;

  const formatAsOfTs = React.useCallback(
    (ts) => {
      const n = Number(ts);
      if (!Number.isFinite(n) || n <= 0) return null;
      return new Date(n * 1000).toLocaleString(language === "en" ? "en-US" : "de-DE", {
        timeZone: "UTC",
      });
    },
    [language]
  );

  const formattedOnchainAsOf = onchainMode === "live"
    ? (language === "en" ? "Live" : "Live")
    : formatAsOfTs(onchainAsOfTs);
  const formattedFifoAsOf = onchainMode === "live"
    ? (language === "en" ? "Live" : "Live")
    : formatAsOfTs(fifoAsOfTs);
  const taxYearRangeLabel = formatTaxYearRangeLabel(
    taxCountryCode || verifierMeta?.selectedCountry || "DE",
    year,
    language
  );

  const dataSourceTitle =
    dataSource === "preview"
      ? tr("balanceVerifier.dataSource.preview")
      : dataSource === "snapshot"
        ? tr("balanceVerifier.dataSource.snapshot")
        : tr("balanceVerifier.dataSource.live");

  const dataSourceShortLabel =
    dataSource === "preview"
      ? language === "en"
        ? "Preview"
        : "Vorschau"
      : dataSource === "snapshot"
        ? "Snapshot"
        : "Live";

  // ✅ DEBUG: Logging für sofortige Diagnose
  React.useEffect(() => {
                                  }, [datasetId, year, snapshotData, state.data, normalizedData, visibleDiffsBase, hiddenSyntheticCount]);

  return (
    <section className={cn(T.shell, "print:hidden min-h-[260px] text-sm")}>
      <div className={cn(T.header, T.headerRail, tone.rail)}>
        <div className={T.headerInner}>
          <div className={T.headerMain}>
            <span className={T.eyebrow}>
              <LayoutDashboard className={cn("h-3.5 w-3.5", tone.eyebrow)} />
              {tr("blockchainBalanceCheck")}
            </span>

            <h3 className={T.title}>{tr("blockchainBalanceCheck")} (Multi-Token)</h3>
            <p className={T.subtitle}>
              {language === "en"
                ? "Reported onchain balances vs. FIFO-calculated inventories across all visible wallets."
                : "Gemeldete Onchain-Balances vs. FIFO-berechnete Bestände über alle sichtbaren Wallets."}
            </p>
          </div>

          <div className={T.headerAsidePills}>
            <span
              className={cn(
                T.countPill,
                "rounded-xl text-[11px] font-semibold",
                dataSource === "preview" && "border-violet-200 bg-violet-50 text-violet-800",
                dataSource === "snapshot" && "border-slate-200 bg-slate-50 text-slate-700",
                dataSource === "live" && "border-emerald-200 bg-emerald-50 text-emerald-800"
              )}
              title={dataSourceTitle}
            >
              {dataSourceShortLabel}
            </span>
          </div>
        </div>
      </div>

      <div className={T.contentInset}>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between rounded-xl border border-slate-200/80 bg-slate-50/80 px-3.5 py-2.5 text-xs shadow-sm">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <>
              {snapshotData && (
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-slate-200/80 bg-white text-slate-500 shadow-sm">
                  <Database className="h-3.5 w-3.5" />
                </span>
              )}
              <span className="font-semibold text-slate-700">{dataSourceTitle}</span>
              {formattedSnapshotTs && (
                <span className="text-slate-500">
                  {language === "en" ? "Calculated at" : "Berechnet am"}:{" "}
                  <span className="font-medium text-slate-700">{formattedSnapshotTs}</span>
                </span>
              )}
              {formattedOnchainAsOf && (
                <span className="text-slate-500">
                  {language === "en" ? "On-chain as of" : "On-chain Stichtag"}:{" "}
                  <span className="font-medium text-slate-700">{formattedOnchainAsOf}</span>
                </span>
              )}
              {formattedFifoAsOf && (
                <span className="text-slate-500">
                  {language === "en" ? "FIFO as of" : "FIFO Stichtag"}:{" "}
                  <span className="font-medium text-slate-700">{formattedFifoAsOf}</span>
                </span>
              )}
              {taxYearRangeLabel && (
                <span className="text-slate-500">
                  {language === "en" ? "Tax year" : "Steuerjahr"}:{" "}
                  <span className="font-medium text-slate-700">{taxYearRangeLabel}</span>
                </span>
              )}
            </>
          </div>

          {/* justify-end auf overflow-x-auto schneidet den linken Überlauf ab → erstes Zeichen von "Ansichtsoptionen" weg */}
          <div className="ml-auto flex min-w-0 w-full max-w-full flex-nowrap items-center justify-start gap-2 overflow-x-auto pl-0.5 sm:w-auto sm:max-w-none sm:gap-2.5 [scrollbar-width:thin]">
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200/80 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 shadow-sm whitespace-nowrap">
              <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" />
              {language === "en" ? "View options" : "Ansichtsoptionen"}
            </span>

            <ToggleChip
              checked={localUseClean}
              disabled={state.loading}
              onClick={() => setLocalUseClean((prev) => !prev)}
              label={tr("balanceVerifier.toggles.cleanView")}
            />

            <ToggleChip
              checked={localIncludeStaked}
              disabled={state.loading}
              onClick={() => setLocalIncludeStaked((prev) => !prev)}
              label={tr("balanceVerifier.toggles.includeStakedVet")}
              hint={!datasetId ? "Preview" : null}
            />
          </div>
        </div>
      </div>

      {Object.keys(softMissing).length > 0 && Object.values(softMissing).some((v) => v === false) && (
        <div className={T.contentInset}>
          <NoticePanel tone="amber" icon={BadgeInfo} title="Optionale Features nicht verfügbar">
            <div className="space-y-1 text-xs">
              {Object.entries(softMissing).map(
                ([key, value]) => !value && <div key={key}>• {key}: nicht verfügbar (optional)</div>
              )}
            </div>
          </NoticePanel>
        </div>
      )}

      {loadFromSnapshot && !snapshotData && (
        <div className={T.contentInset}>
          <NoticePanel tone="amber" icon={CircleAlert} title="Snapshot enthält keine Balance-Verifier-Daten">
            Bitte Analyse erneut ausführen.
          </NoticePanel>
        </div>
      )}

      {state.error && (
        <div className={T.contentInset}>
          <NoticePanel tone="rose" icon={CircleAlert} title="Fehler beim Balance-Abgleich">
            ❌ {state.error}
          </NoticePanel>
        </div>
      )}

      {state.loading && (
        <div className={T.contentInset}>
          <div className="mb-4 rounded-2xl border border-slate-200/80 bg-white/90 px-4 py-8 text-center text-slate-600 shadow-sm">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-b-indigo-500" />
            <div className="text-sm font-semibold text-slate-800">
              {language === "en" ? "Balance reconciliation in progress..." : "Balance-Abgleich läuft..."}
            </div>
          </div>
        </div>
      )}

      {d && !state.loading && d.fifoTotals && Object.keys(d.fifoTotals).length === 0 && (
        <div className={T.contentInset}>
          <NoticePanel tone="amber" icon={CircleAlert} title="Keine FIFO-Daten verfügbar">
            <div className="text-xs leading-6">
              <p>Die FIFO-Berechnungsergebnisse konnten nicht geladen werden.</p>
              <p className="mt-2 font-semibold">Mögliche Ursachen:</p>
              <ul className="ml-5 mt-1 list-disc space-y-1">
                <li>Die Hauptanalyse wurde noch nicht ausgeführt</li>
                <li>Das Wallet-Set wurde nach der Analyse geändert</li>
                <li>Der Cache wurde geleert und kein Snapshot existiert</li>
              </ul>
              <p className="mt-2">
                <strong>Lösung:</strong> Bitte führen Sie die Hauptanalyse erneut aus.
              </p>
            </div>
          </NoticePanel>
        </div>
      )}

      {!normalizedData && !state.loading && !state.error && (
        <div className={T.contentInset}>
          <NoticePanel tone="slate" icon={BadgeInfo} title="Warte auf Balance-Verifier-Daten">
            <span className="text-sm text-slate-600">
              Die Balance-Verifier-Daten werden geladen oder die Analyse läuft noch.
            </span>
          </NoticePanel>
        </div>
      )}

      {normalizedData && !state.loading && visibleDiffsBase.length === 0 && (() => {
        const hasAnyTotals =
          (normalizedData.fifoTotals && Object.keys(normalizedData.fifoTotals).length > 0) ||
          (normalizedData.onchainTotals && Object.keys(normalizedData.onchainTotals).length > 0);

        if (hasAnyTotals) {
          return (
            <div className={T.contentInset}>
              <NoticePanel tone="emerald" icon={ShieldCheck} title="Keine sichtbaren Abweichungen gefunden">
                <span className="text-sm text-emerald-900">
                  On-chain und FIFO stimmen für alle sichtbaren Tokens überein.
                </span>
              </NoticePanel>
            </div>
          );
        }

        return (
          <div className={T.contentInset}>
            <NoticePanel tone="amber" icon={CircleAlert} title="Keine Balance-Verifier-Daten verfügbar">
              <div className="text-xs leading-6">
                <p><strong>Mögliche Ursachen:</strong></p>
                <ul className="ml-5 mt-1 list-disc space-y-1">
                  <li>Die Hauptanalyse wurde noch nicht mit FIFO-Berechnung ausgeführt</li>
                  <li>Keine Token-Unterschiede zwischen On-chain und FIFO gefunden</li>
                  <li>Die Datenstruktur entspricht nicht dem erwarteten Format</li>
                </ul>

                <div className="mt-3 rounded-xl border border-amber-200 bg-white/70 p-3">
                  <div className="mb-1 font-semibold">Debug-Info</div>
                  <pre className="overflow-auto text-[11px] text-slate-700">
                    {JSON.stringify(
                      {
                        hasData: !!normalizedData,
                        hasDiffs: !!normalizedData.diffs,
                        diffsLength: normalizedData.diffs?.length || 0,
                        visibleDiffsLength: visibleDiffsBase.length,
                        hiddenSyntheticCount,
                        hasFifoTotals: !!normalizedData.fifoTotals,
                        fifoTotalsKeys: normalizedData.fifoTotals
                          ? Object.keys(normalizedData.fifoTotals).slice(0, 5)
                          : [],
                        hasOnchainTotals: !!normalizedData.onchainTotals,
                        onchainTotalsKeys: normalizedData.onchainTotals
                          ? Object.keys(normalizedData.onchainTotals).slice(0, 5)
                          : [],
                        rawDataKeys: d ? Object.keys(d).slice(0, 10) : [],
                      },
                      null,
                      2
                    )}
                  </pre>
                </div>
              </div>
            </NoticePanel>
          </div>
        );
      })()}

      {normalizedData && !state.loading && visibleDiffsBase.length > 0 && (() => {
        const filteredDiffs = localUseClean
          ? visibleDiffsBase.filter((row) => {
              const maxAmount = Math.max(Math.abs(co(row.onchain)), Math.abs(co(row.fifo)));
              return maxAmount >= 1.0;
            })
          : visibleDiffsBase;

        if (filteredDiffs.length === 0 && localUseClean && visibleDiffsBase.length > 0) {
          return (
            <div className={T.contentInset}>
              <NoticePanel tone="sky" icon={SlidersHorizontal} title="Clean-View aktiv">
                <div className="text-sm">
                  Alle Token wurden ausgeblendet (Betrag &lt; 1.0). Deaktiviere „Clean-View“, um alle Token zu sehen.
                </div>
                <div className="mt-2 text-xs text-slate-600">
                  <strong>Gesamt gefunden:</strong> {visibleDiffsBase.length} Token
                  {hiddenSyntheticCount > 0 && (
                    <span className="ml-2">
                      · <strong>Synthetisch ausgeblendet:</strong> {hiddenSyntheticCount}
                    </span>
                  )}
                </div>
              </NoticePanel>
            </div>
          );
        }

        return (
          <>
            <div className={cn(T.tableBlock, "hidden md:block")}>
              <div
                className={cn(
                  T.tableWrap,
                  "ring-1 ring-indigo-100/80 shadow-[0_12px_30px_-22px_rgba(79,70,229,0.35),inset_0_1px_0_rgba(255,255,255,0.85)]"
                )}
              >
                <table className={cn(T.table, "min-w-[1080px] table-fixed")}>
                  <thead className="bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 text-white shadow-[inset_0_-1px_0_rgba(255,255,255,0.14)]">
                    <tr>
                      <th className={cn(T.th, "align-middle w-[16%] text-left text-[12px] font-semibold normal-case tracking-normal text-white")}>
                        {tr("balanceVerifier.tableHeaders.token")}
                      </th>

                      <th className={cn(T.th, "align-middle w-[17%] text-right")}>
                        <div className="flex flex-col items-end leading-tight">
                          <span className="text-[12px] font-semibold normal-case tracking-normal text-white">
                            {tr("balanceVerifier.tableHeaders.onchain")}
                          </span>
                          <span className="mt-1 text-[10px] font-medium normal-case tracking-normal text-white/78">
                            {tr("balanceVerifier.tableHeaders.onchainSub")}
                          </span>
                        </div>
                      </th>

                      <th className={cn(T.th, "align-middle w-[17%] text-right")}>
                        <div className="flex flex-col items-end leading-tight">
                          <span className="text-[12px] font-semibold normal-case tracking-normal text-white">
                            {tr("balanceVerifier.tableHeaders.fifo")}
                          </span>
                          <span className="mt-1 text-[10px] font-medium normal-case tracking-normal text-white/78">
                            {tr("balanceVerifier.labels.walletOnly")}
                          </span>
                        </div>
                      </th>

                      <th className={cn(T.th, "align-middle w-[16%] text-right")}>
                        <div className="flex flex-col items-end leading-tight">
                          <span className="text-[12px] font-semibold normal-case tracking-normal text-white">
                            {tr("balanceVerifier.tableHeaders.delta")}
                          </span>
                          <span className="mt-1 text-[10px] font-medium normal-case tracking-normal text-white/78">
                            {tr("balanceVerifier.tableHeaders.deltaSub")}
                          </span>
                        </div>
                      </th>

                      <th className={cn(T.th, "align-middle w-[18%] text-right")} title={tr("balanceVerifier.descriptions.deltaTurnover")}>
                        <div className="flex flex-col items-end leading-tight">
                          <span className="text-[12px] font-semibold normal-case tracking-normal text-white">
                            {tr("balanceVerifier.tableHeaders.activity")}
                          </span>
                          <span className="mt-1 text-[10px] font-medium normal-case tracking-normal text-white/78">
                            {tr("balanceVerifier.tableHeaders.activitySub")}
                          </span>
                        </div>
                      </th>

                      <th className={cn(T.th, "align-middle w-[16%] text-center text-[12px] font-semibold normal-case tracking-normal text-white")}>
                        {tr("balanceVerifier.tableHeaders.status")}
                      </th>
                    </tr>
                  </thead>

                  <tbody className={T.tbody}>
                    {filteredDiffs.map((row, idx) => {
                      const onchain = co(row.onchain);
                      const fifo = co(row.fifo);
                      const delta = co(row.delta);
                      const turnover = co(row.turnover);
                      const pctMove = row.pct_move;
                      const isMatch = row.status === "match" || Math.abs(delta) < 1e-8;

                      const deltaTone =
                        Math.abs(delta) < 1e-8
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : delta > 0
                            ? "border-amber-200 bg-amber-50 text-amber-800"
                            : "border-indigo-200 bg-indigo-50 text-indigo-700";

                      const dotTone =
                        isMatch
                          ? "bg-emerald-500"
                          : row.status === "onchain_higher"
                            ? "bg-amber-500"
                            : "bg-indigo-500";

                      const striped = idx % 2 === 0 ? "bg-white" : "bg-slate-50/35";

                      return (
                        <tr key={row.token || idx} className={cn(T.tr, striped, "hover:bg-slate-100/80")}>
                          <td className={cn(T.td, "align-middle")}>
                            <div className="min-w-0">
                              <div className="inline-flex max-w-full items-center gap-2 rounded-xl border border-slate-200/80 bg-white px-3 py-2 shadow-sm">
                                <span className={`h-2 w-2 rounded-full ${dotTone}`} />
                                <span className="truncate font-semibold text-slate-900">
                                  {row.token || "UNKNOWN"}
                                </span>
                              </div>

                              {Array.isArray(row.lp_breakdown) && row.lp_breakdown.length > 0 && (
                                <div
                                  className="mt-2 truncate text-[11px] text-slate-500"
                                  title={row.lp_breakdown.map((p) => p.pool).join(", ")}
                                >
                                  Pools: {row.lp_breakdown.slice(0, 2).map((p) => shortAddr(p.pool)).join(", ")}
                                  {row.lp_breakdown.length > 2 ? ` +${row.lp_breakdown.length - 2}` : ""}
                                </div>
                              )}
                            </div>
                          </td>

                          <td className={cn(T.td, "align-middle text-right")}>
                            <div className="font-mono text-[13px] font-semibold tabular-nums text-slate-900">
                              {fmtTokenAmountFixed(onchain, 6)}
                            </div>
                          </td>

                          <td className={cn(T.td, "align-middle text-right")}>
                            <div className="font-mono text-[13px] font-semibold tabular-nums text-slate-900">
                              {fmtTokenAmountFixed(fifo, 6)}
                            </div>
                            {Number.isFinite(row.fifo_full) && Math.abs(row.fifo_full - fifo) > 1e-8 && (
                              <div className="mt-1 text-[11px] text-slate-400">
                                {tr("balanceVerifier.labels.fifoFullShort")}{" "}
                                {fmtTokenAmountFixed(Number(row.fifo_full), 6)}
                              </div>
                            )}
                          </td>

                          <td className={cn(T.td, "align-middle text-right")}>
                            <span
                              className={cn(
                                "inline-flex min-w-[96px] items-center justify-center rounded-full border px-3 py-1.5 font-mono text-[12px] font-semibold shadow-sm",
                                deltaTone
                              )}
                            >
                              {delta > 0 ? "+" : ""}
                              {fmtTokenAmountFixed(delta, 6)}
                            </span>
                          </td>

                          <td className={cn(T.td, "align-middle text-right")}>
                            <div className="text-[13px] font-semibold text-slate-900">
                              {turnover > 0 ? fmtTurnover(turnover) : "—"}
                            </div>
                            <div className="mt-1 text-[11px] font-medium text-slate-500">
                              Δ/Turnover · {pctMove != null ? fmtPctSmart(pctMove) : "—"}
                            </div>
                          </td>

                          <td className={cn(T.td, "align-middle text-center")}>
                            <div className="flex justify-center">
                              <StatusBadge status={row.status} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={cn(T.contentInset, "md:hidden space-y-3")}>
              {filteredDiffs.map((row, idx) => {
                const onchain = co(row.onchain);
                const fifo = co(row.fifo);
                const delta = co(row.delta);
                const turnover = co(row.turnover);
                const pctMove = row.pct_move == null ? null : Number(row.pct_move);

                const deltaTextClass =
                  Math.abs(delta) < 1e-8
                    ? "text-emerald-700"
                    : delta > 0
                      ? "text-amber-800"
                      : "text-indigo-700";

                return (
                  <div
                    key={`diff-mobile-${row.token || idx}`}
                    className="rounded-[1.15rem] border border-slate-200/80 bg-white/95 p-4 shadow-sm ring-1 ring-white/70"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900">
                          {row.token || "UNKNOWN"}
                        </div>

                        {Array.isArray(row.lp_breakdown) && row.lp_breakdown.length > 0 && (
                          <div
                            className="mt-1 truncate text-[11px] text-slate-500"
                            title={row.lp_breakdown.map((p) => p.pool).join(", ")}
                          >
                            Pools: {row.lp_breakdown.slice(0, 2).map((p) => shortAddr(p.pool)).join(", ")}
                            {row.lp_breakdown.length > 2 ? ` +${row.lp_breakdown.length - 2}` : ""}
                          </div>
                        )}
                      </div>

                      <StatusBadge status={row.status} compact />
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 px-3 py-3">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                          On-chain
                        </div>
                        <div className="mt-2 font-mono text-sm text-slate-900">
                          {fmtTokenAmountFixed(onchain, 6)}
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 px-3 py-3 text-right">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                          FIFO
                        </div>
                        <div className="mt-2 font-mono text-sm text-slate-900">
                          {fmtTokenAmountFixed(fifo, 6)}
                        </div>
                        {Number.isFinite(row.fifo_full) && Math.abs(row.fifo_full - fifo) > 1e-8 && (
                          <div className="mt-1 text-[11px] text-slate-400">
                            {tr("balanceVerifier.labels.fifoFullShort")}{" "}
                            {fmtTokenAmountFixed(Number(row.fifo_full), 6)}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 flex items-end justify-between gap-3">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                          Δ
                        </div>
                        <div className={`mt-1 font-mono text-sm font-semibold ${deltaTextClass}`}>
                          {delta > 0 ? "+" : ""}
                          {fmtTokenAmountFixed(delta, 6)}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                          Activity
                        </div>
                        <div className="mt-1 font-mono text-sm text-slate-900">
                          {turnover > 0 ? fmtTurnover(turnover) : "—"}
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          Δ/Turnover · {pctMove != null ? fmtPctSmart(pctMove) : "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={T.contentInset}>
              <div className="mt-4 rounded-2xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/70 px-4 py-4 shadow-sm">
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  <SummaryMetric
                    dotClass="bg-slate-500"
                    label={tr("balanceVerifier.summary.tokensCompared")}
                    value={
                      localUseClean && filteredDiffs.length !== visibleDiffsBase.length
                        ? `${filteredDiffs.length}`
                        : `${filteredDiffs.length}`
                    }
                    hint={
                      [
                        localUseClean && filteredDiffs.length !== visibleDiffsBase.length
                          ? `${visibleDiffsBase.length - filteredDiffs.length} ${tr("balanceVerifier.summary.hidden")}`
                          : null,
                        hiddenSyntheticCount > 0 ? `synthetic hidden: ${hiddenSyntheticCount}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || null
                    }
                  />

                  <SummaryMetric
                    dotClass="bg-emerald-500"
                    label={tr("balanceVerifier.summary.matches")}
                    value={`${filteredDiffs.filter((r) => r.status === "match").length}`}
                  />

                  <SummaryMetric
                    dotClass="bg-amber-500"
                    label={tr("balanceVerifier.summary.onchainHigher")}
                    value={`${filteredDiffs.filter((r) => r.status === "onchain_higher").length}`}
                  />

                  <SummaryMetric
                    dotClass="bg-indigo-500"
                    label={tr("balanceVerifier.summary.fifoHigher")}
                    value={`${filteredDiffs.filter((r) => r.status === "fifo_higher").length}`}
                  />
                </div>
              </div>
            </div>

            <div className={T.contentInset}>
              <div className="mt-4 rounded-2xl border border-slate-200/80 bg-white px-4 py-4 shadow-sm">
                <div className="mb-4 flex items-center gap-2 text-slate-900">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200/80 bg-slate-50 text-slate-600 shadow-sm">
                    <BadgeInfo className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{tr("balanceVerifier.title")}</div>
                    <div className="text-xs text-slate-500">
                      {language === "en"
                        ? "Legend and interpretation of the reconciliation view"
                        : "Legende und Begriffserklärung für den Abgleich"}
                    </div>
                  </div>
                </div>

                <div className="mb-4 flex flex-wrap gap-2 rounded-xl border border-slate-200/80 bg-slate-50/70 p-3">
                  <StatusBadge status="match" />
                  <StatusBadge status="onchain_higher" />
                  <StatusBadge status="fifo_higher" />
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <InfoItem
                    title={tr("balanceVerifier.terms.onchainReported")}
                    tone="slate"
                    icon={Database}
                  >
                    {tr("balanceVerifier.descriptions.onchainReported")}
                  </InfoItem>

                  <InfoItem
                    title={tr("balanceVerifier.terms.fifoCalculated")}
                    tone="indigo"
                    icon={ShieldCheck}
                  >
                    {tr("balanceVerifier.descriptions.fifoCalculated")}
                  </InfoItem>

                  <InfoItem
                    title={tr("balanceVerifier.terms.deltaDifference")}
                    tone="amber"
                    icon={SlidersHorizontal}
                  >
                    {tr("balanceVerifier.descriptions.deltaDifference")}
                  </InfoItem>

                  <InfoItem
                    title={tr("balanceVerifier.terms.activityTurnover")}
                    tone="slate"
                    icon={Activity}
                  >
                    {tr("balanceVerifier.descriptions.activityTurnover")}
                  </InfoItem>

                  <InfoItem
                    title={tr("balanceVerifier.terms.deltaTurnover")}
                    tone="indigo"
                    icon={CircleAlert}
                    className="md:col-span-2"
                  >
                    {tr("balanceVerifier.descriptions.deltaTurnover")}
                  </InfoItem>
                </div>
              </div>
            </div>
          </>
        );
      })()}
    </section>
  );
}

